import {
  Box,
  Disclaimer,
  Div,
  H5,
  ReceivablesLocation,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { Link } from 'react-router-dom';
import { formatDay } from '../helpers/receivablesLabels';
import {
  ARROW_COLUMN,
  InactiveTag,
  LocationNameLink,
  OwnerName,
  RowArrow,
} from './ReceivablesCells';
import { ReceivablesLocationCard } from './ReceivablesLocationCard';
import {
  BankStatus,
  OldVDriveDebt,
  OwedBreakdown,
  SaasVisibility,
} from './ReceivablesLocationValues';

type Props = {
  row: ReceivablesLocation;
  /** Where the row leads: the location's receivables page. */
  to: string;
};

/** Widths shared with the list's TableHeader, so the columns line up. */
export const RECEIVABLES_COLUMNS = [
  { label: 'Location', width: '16%' },
  { label: 'Owner', width: '11%' },
  { label: 'Debt by Service', width: '17%' },
  {
    // Two lines on purpose: one does not fit the 14% column.
    label: (
      <>
        Previous Transaction
        <br />
        Due Balance
      </>
    ),
    width: '14%',
  },
  { label: 'Bank', width: '14%' },
  { label: 'SaaS', width: '14%' },
  { label: 'Owed', width: '12%' },
  ARROW_COLUMN,
];

/**
 * One location in the receivables list: what it owes, what for, where its
 * bank stands and whether the owner is shown the SaaS part at all.
 *
 * Columns on desktop; on phones the same values as a card
 * (ReceivablesLocationCard).
 *
 * A real link, not a click handler, so cmd/ctrl-click, a middle click and
 * the context menu's "Open in new tab" all work - going down the list one
 * location at a time is easier from tabs.
 */
export const ReceivablesLocationRow = ({ row, to }: Props) => (
  <Box
    as={Link}
    to={to}
    style={{ color: 'inherit', textDecoration: 'none' }}
    $mb="8"
    $flex
    $alignCenter
    // 20px, the same as the list's header: with the Old vDrive Debt column
    // seven columns share the row, and 32px left the amounts cramped.
    $gap="20px"
    $pointer
    // An inactive (deleted) location is outlined in red, on top of the tag
    // under its name, so it stands out while scrolling the amounts.
    $borderColor={row.deleted ? lightTheme.primary : undefined}
  >
    <Div $desktop $width={RECEIVABLES_COLUMNS[0].width} $minWidth="0">
      <Div $flex $column $gap="4px" $minWidth="0">
        <LocationNameLink locationId={row.locationId} name={row.locationName} />
        {row.deleted && <InactiveTag />}
      </Div>
    </Div>
    <Div $desktop $width={RECEIVABLES_COLUMNS[1].width} $minWidth="0">
      <OwnerName name={row.ownerName} />
    </Div>

    <Div $desktop $width={RECEIVABLES_COLUMNS[2].width}>
      {/* One service per line. */}
      <Div $flex $column $gap="2px" $minWidth="0">
        <OwedBreakdown row={row} />
      </Div>
    </Div>

    {/* Old vDrive debt on its own: it is what a repayment plan pays back,
        so the plan's tag sits beside it here and not between the other
        services' amounts. */}
    <Div $desktop $width={RECEIVABLES_COLUMNS[3].width}>
      <OldVDriveDebt row={row} />
    </Div>

    <Div $desktop $width={RECEIVABLES_COLUMNS[4].width}>
      <BankStatus row={row} />
    </Div>

    <Div $desktop $width={RECEIVABLES_COLUMNS[5].width}>
      <SaasVisibility row={row} />
    </Div>

    <Div $desktop $width={RECEIVABLES_COLUMNS[6].width}>
      <H5 $m="0" $color={row.total > 0 ? lightTheme.primary : undefined}>
        {displayCentsInDollars(row.total)}
      </H5>
      {row.oldestItemAt && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          Oldest {formatDay(row.oldestItemAt)}
        </Disclaimer>
      )}
    </Div>
    <RowArrow />

    <ReceivablesLocationCard row={row} />
  </Box>
);
